import { useRandomId, useOnClickOutside, warnAboutMissingStyles } from '@entur/utils';
import React, { useState, cloneElement, createContext, useContext } from 'react';
import { Manager, Reference, Popper, usePopper } from 'react-popper';
import classNames from 'classnames';
import { CloseIcon } from '@entur/icons';
import { IconButton } from '@entur/button';
import { Contrast } from '@entur/layout';
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var _excluded$1 = ["placement", "content", "children", "className", "isOpen", "disableHoverListener", "disableFocusListener", "showCloseButton", "variant", "popperModifiers"];
/** @deprecated use variant="negative" instead */
var error = 'error';
var Tooltip = function Tooltip(_ref) {
  var placement = _ref.placement,
    content = _ref.content,
    children = _ref.children,
    className = _ref.className,
    _ref$isOpen = _ref.isOpen,
    isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
    _ref$disableHoverList = _ref.disableHoverListener,
    disableHoverListener = _ref$disableHoverList === void 0 ? false : _ref$disableHoverList,
    _ref$disableFocusList = _ref.disableFocusListener,
    disableFocusListener = _ref$disableFocusList === void 0 ? false : _ref$disableFocusList,
    _ref$showCloseButton = _ref.showCloseButton,
    showCloseButton = _ref$showCloseButton === void 0 ? true : _ref$showCloseButton,
    variant = _ref.variant,
    _ref$popperModifiers = _ref.popperModifiers,
    popperModifiers = _ref$popperModifiers === void 0 ? [{
      name: 'offset',
      options: {
        offset: [0, 10]
      }
    }] : _ref$popperModifiers,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var _useState = useState(isOpen || false),
    showTooltip = _useState[0],
    setShowTooltip = _useState[1];
  var hoverTimer;
  var handleOpen = function handleOpen(event) {
    event.persist();
    hoverTimer = setTimeout(function () {
      setShowTooltip(true);
    }, 150);
  };
  var onMouseExit = function onMouseExit() {
    setShowTooltip(false);
    clearTimeout(hoverTimer);
  };
  React.useEffect(function () {
    return function () {
      clearTimeout(hoverTimer);
    };
  });
  var tooltipId = useRandomId('eds-tooltip');
  React.useEffect(function () {
    setShowTooltip(isOpen);
  }, [isOpen]);
  React.useEffect(function () {
    if (!content) {
      setShowTooltip(false);
    }
  }, [content]);
  var popperPlacement = placement;
  if (placement.includes('-')) {
    if (placement.includes('right')) {
      popperPlacement = placement.replace('right', 'end');
    }
    if (placement.includes('left')) {
      popperPlacement = placement.replace('left', 'start');
    }
  }
  var childProps = {};
  childProps['aria-describedby'] = showTooltip ? tooltipId : undefined;
  if (!disableFocusListener) {
    childProps.onFocus = function () {
      return setShowTooltip(true);
    };
    childProps.onBlur = function () {
      return setShowTooltip(false);
    };
  }
  if (!disableHoverListener) {
    childProps.onMouseEnter = function (e) {
      return handleOpen(e);
    };
    childProps.onMouseLeave = function () {
      return onMouseExit();
    };
  }
  return React.createElement(Manager, null, React.createElement(Reference, null, function (_ref2) {
    var ref = _ref2.ref;
    return cloneElement(children, _extends({
      ref: ref
    }, childProps));
  }), showTooltip && React.createElement(Popper, {
    modifiers: [{
      name: 'arrow',
      enabled: false
    }].concat(popperModifiers),
    placement: popperPlacement
  }, function (_ref3) {
    var ref = _ref3.ref,
      style = _ref3.style,
      popperPlacement = _ref3.placement;
    return React.createElement("div", _extends({
      className: classNames('eds-tooltip', className, "eds-tooltip--" + popperPlacement, {
        'eds-tooltip--negative': variant === error || variant === 'negative'
      }),
      ref: ref,
      style: style,
      role: "tooltip",
      id: tooltipId,
      "data-placement": popperPlacement
    }, rest), content, isOpen && showCloseButton && React.createElement(IconButton, {
      className: "eds-tooltip__close-button",
      onClick: function onClick() {
        return setShowTooltip(false);
      },
      type: "button"
    }, React.createElement(CloseIcon, null)));
  }));
};
var _excluded = ["children"];
var PopoverContext = /*#__PURE__*/createContext(undefined);
var usePopoverContext = function usePopoverContext() {
  var context = useContext(PopoverContext);
  if (context == null) {
    throw Error('usePopoverContext must be used within <Popover/>');
  }
  return context;
};
var useCustomState = function useCustomState(state, setState) {
  var _React$useState = React.useState(false),
    internalState = _React$useState[0],
    setInternalState = _React$useState[1];
  var controlled = state !== undefined && setState !== undefined;
  if (controlled) return [state, setState, controlled];
  return [internalState, setInternalState, controlled];
};
var Popover = function Popover(_ref) {
  var children = _ref.children,
    _ref$placement = _ref.placement,
    placement = _ref$placement === void 0 ? 'bottom-start' : _ref$placement,
    controlledState = _ref.showPopover,
    setControlledState = _ref.setShowPopover;
  var _useCustomState = useCustomState(controlledState, setControlledState),
    showPopover = _useCustomState[0],
    setShowPopover = _useCustomState[1],
    controlled = _useCustomState[2];
  var triggerElement = React.useRef(null);
  var contentElement = React.useRef(null);
  var _usePopper = usePopper(triggerElement.current, contentElement.current, {
      modifiers: [{
        name: 'arrow',
        enabled: false
      }, {
        name: 'offset',
        options: {
          offset: [0, 8]
        }
      }],
      placement: placement
    }),
    styles = _usePopper.styles,
    attributes = _usePopper.attributes,
    forceUpdate = _usePopper.forceUpdate;
  React.useEffect(function () {
    if (forceUpdate) {
      forceUpdate();
    }
  }, [showPopover, forceUpdate]);
  var triggerProps = React.useCallback(function () {
    var buttonProps = {
      'aria-haspopup': 'dialog',
      'aria-expanded': showPopover,
      ref: triggerElement,
      type: 'button'
    };
    var buttonOnClick = {
      onClick: function onClick(e) {
        e.preventDefault();
        setShowPopover(function (prev) {
          return !prev;
        });
      }
    };
    return controlled ? buttonProps : _extends({}, buttonProps, buttonOnClick);
  }, [triggerElement, showPopover, setShowPopover, controlled]);
  useOnClickOutside([contentElement, triggerElement], function () {
    return setShowPopover(false);
  });
  var closeButtonProps = {
    onClick: function onClick(e) {
      e.preventDefault();
      setShowPopover(false);
    },
    type: 'button'
  };
  var popoverContentProps = React.useCallback(function () {
    var contentProps = {
      role: 'dialog',
      'aria-modal': 'false',
      ref: contentElement,
      onKeyDown: function onKeyDown(event) {
        if (event.key === 'Escape') {
          showPopover && setShowPopover(false);
        }
      },
      onBlur: function onBlur(event) {
        var elementReceivingFocus = event.relatedTarget;
        // The check for 'tabindex=-1' is a special case for focus handling in Docz
        if (!elementReceivingFocus || elementReceivingFocus.getAttribute('tabindex') === '-1') return;
        var focusElementIsPopover = elementContainsElement(contentElement.current, elementReceivingFocus);
        var focusElementIsTrigger = elementContainsElement(triggerElement.current, elementReceivingFocus);
        var isValidBlur = !focusElementIsPopover && !focusElementIsTrigger;
        if (showPopover && isValidBlur) setShowPopover(false);
      }
    };
    return contentProps;
  }, [contentElement, showPopover, setShowPopover]);
  var contextValue = {
    showPopover: showPopover,
    triggerElement: triggerElement,
    contentElement: contentElement,
    styles: styles,
    attributes: attributes,
    popoverContentProps: popoverContentProps,
    closeButtonProps: closeButtonProps,
    triggerProps: triggerProps
  };
  return React.createElement(PopoverContext.Provider, {
    value: contextValue
  }, children);
};
var PopoverTrigger = function PopoverTrigger(_ref2) {
  var children = _ref2.children;
  var _usePopoverContext = usePopoverContext(),
    triggerProps = _usePopoverContext.triggerProps;
  var child = React.Children.only(children);
  return cloneElement(child, triggerProps());
};
var PopoverCloseButton = function PopoverCloseButton(_ref3) {
  var children = _ref3.children,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded);
  var _usePopoverContext2 = usePopoverContext(),
    closeButtonProps = _usePopoverContext2.closeButtonProps;
  return cloneElement(children, _extends({}, closeButtonProps, rest));
};
var PopoverContent = /*#__PURE__*/React.forwardRef(function (_ref4, ref) {
  var children = _ref4.children;
  var _usePopoverContext3 = usePopoverContext(),
    showPopover = _usePopoverContext3.showPopover,
    attributes = _usePopoverContext3.attributes,
    styles = _usePopoverContext3.styles,
    popoverContentProps = _usePopoverContext3.popoverContentProps;
  var props = popoverContentProps({
    ref: ref
  });
  return React.createElement(Contrast, _extends({
    className: classNames('eds-popover', {
      'eds-popover--hidden': !showPopover
    }, 'eds-contrast'),
    style: styles.popper,
    "aria-hidden": !showPopover
  }, attributes.styles, props), children);
});
function elementContainsElement(parent, child) {
  if (!parent) return false;
  return parent === child || parent.contains(child);
}
warnAboutMissingStyles('tooltip');
export { Popover, PopoverCloseButton, PopoverContent, PopoverTrigger, Tooltip };